import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useServices, Service as ServiceType, ServiceCategory as ServiceCategoryType } from '../contexts/services/ServiceContext';
import { useTranslation } from 'react-i18next';
import SEO from '../components/SEO';

// Define types for our data
type Service = ServiceType;

type ServiceCategory = ServiceCategoryType;

// Service card component
const ServiceCard = ({ service }: { service: any }) => {
  const navigate = useNavigate();
  const { t } = useTranslation('common');
  
  const handleBookNow = () => {
    // Use discounted price if available, otherwise use regular price
    let price = typeof service.numericPrice === 'number' ? service.numericPrice : 0;
    
    // Check if discountedPrice exists and use it instead
    if (service.discountedPrice) {
      const discountedPriceNum = parseFloat(service.discountedPrice.toString().replace(/[^0-9.]/g, ''));
      if (!isNaN(discountedPriceNum)) {
        price = discountedPriceNum;
      }
    }
    
    const duration = typeof service.numericDuration === 'number' ? service.numericDuration : 0;

    // Store the selected service in localStorage to retrieve it in the booking page
    localStorage.setItem('selectedService', JSON.stringify({
      id: service.id,
      name: service.name,
      duration: duration,
      price: price,
      category_id: service.category_id
    }));
    
    // Navigate to the booking page
    navigate('/booking');
  };
  
  // Function to parse and format description with special notes
  const formatDescription = (description: string) => {
    if (!description) return null;
    
    // Split by double newlines to get paragraphs
    const paragraphs = description.split('\n\n');
    
    return paragraphs.map((paragraph, idx) => {
      // Remove markdown bold syntax for display
      const cleanText = paragraph.replace(/\*\*/g, '');
      
      // Check if this paragraph starts with special keywords that should be highlighted
      const notePatterns = ['Note:', 'Important:', 'Ladies Only'];
      const startsWithNote = notePatterns.some(pattern => cleanText.trim().startsWith(pattern));
      
      if (startsWithNote) {
        // This is a note line - display it in a badge
        return (
          <div key={idx} className="mt-3 pt-3 border-t border-[#B2D8C5]/30">
            <p className="text-xs font-semibold text-[#8B6F47] bg-[#FFF8E7] px-2 py-1 rounded inline-block">
              {cleanText}
            </p>
          </div>
        );
      }
      
      // Regular paragraph - normal styling
      return (
        <p key={idx} className={`text-[#6B7280] ${idx > 0 ? 'mt-2' : ''}`}>
          {cleanText}
        </p>
      );
    });
  };
  
  return (
    <div className="bg-white/80 backdrop-blur-sm rounded-lg shadow-md p-6 flex flex-col hover:shadow-xl transition-shadow">
      <h3 className="text-lg font-semibold mb-2 text-[#232347] font-serif">{service.name}</h3>
      <div className="text-[#6B7280] mb-4 text-sm flex-grow">
        {formatDescription(service.description)}
      </div>
      <div className="flex justify-between items-center text-[#232347] mt-auto">
        <span className="font-medium text-sm">{service.duration}</span>
        <div>
          {service.discountedPrice ? (
            <div className="flex flex-col items-end">
              <span className="text-sm line-through text-gray-400">{service.price}</span>
              <span className="font-bold text-red-500">{service.discountedPrice}</span>
            </div>
          ) : (
            <span className="font-bold">{service.price}</span>
          )}
        </div>
      </div>
      <button 
        onClick={handleBookNow}
        className="mt-4 bg-[#B2D8C5] text-white px-5 py-2 rounded-md text-sm font-medium shadow hover:bg-[#88bfa3] transition-colors"
      >
        {t('buttons.bookNow')}
      </button>
    </div>
  );
};

function Services() {
  const { categories: serviceCategories, isLoading, error } = useServices();
  const navigate = useNavigate();
  const { t } = useTranslation(['services', 'common']);

  // Loading state
  if (isLoading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-[#F8F6F2] via-[#E8F5F2] to-[#F9E9E9] font-sans flex items-center justify-center">
        <div className="text-center">
          <div className="inline-block w-12 h-12 border-4 border-[#B2D8C5] border-t-[#232347] rounded-full animate-spin mb-4"></div>
          <p className="text-[#232347]">{t('common:common.loading')}</p>
        </div>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-[#F8F6F2] via-[#E8F5F2] to-[#F9E9E9] font-sans flex items-center justify-center">
        <div className="bg-white/80 backdrop-blur-sm rounded-lg shadow-lg p-8 max-w-md">
          <h2 className="text-2xl font-serif text-red-500 mb-4">Oops, something went wrong</h2>
          <p className="text-[#232347]/80 mb-6">{error}</p>
          <button 
            onClick={() => window.location.reload()} 
            className="bg-[#B2D8C5] text-white px-5 py-2 rounded-md text-sm font-medium shadow hover:bg-[#88bfa3] transition-colors"
          >
            Try Again
          </button>
        </div>
      </div>
    );
  }

  // Empty state
  if (serviceCategories.length === 0) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-[#F8F6F2] via-[#E8F5F2] to-[#F9E9E9] font-sans flex items-center justify-center">
        <div className="bg-white/80 backdrop-blur-sm rounded-lg shadow-lg p-8 max-w-md text-center">
          <h2 className="text-2xl font-serif text-[#232347] mb-4">No Services Available</h2>
          <p className="text-[#232347]/80 mb-6">We're currently updating our service offerings. Please check back soon!</p>
        </div>
      </div>
    );
  }

  // Main content
  return (
    <div className="min-h-screen bg-gradient-to-br from-[#F8F6F2] via-[#E8F5F2] to-[#F9E9E9] font-sans">
      <SEO
        title="Spa Services in Dallas — Massage, Facials, Body Treatments | Healing Lotus"
        description="Explore massage therapy, anti-aging facials, body treatments, and specialty spa services at Healing Lotus Skin Spa in Dallas, TX. Book online in minutes."
        path="/services"
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'ItemList',
            itemListElement: [
              { '@type': 'Service', name: 'Massage Therapy', serviceType: 'Massage', provider: { '@type': 'HealthAndBeautyBusiness', name: 'Healing Lotus Skin Spa' }, areaServed: { '@type': 'City', name: 'Dallas' } },
              { '@type': 'Service', name: 'Facial Treatments', serviceType: 'Facial', provider: { '@type': 'HealthAndBeautyBusiness', name: 'Healing Lotus Skin Spa' }, areaServed: { '@type': 'City', name: 'Dallas' } },
              { '@type': 'Service', name: 'Body Treatments', serviceType: 'Body Treatment', provider: { '@type': 'HealthAndBeautyBusiness', name: 'Healing Lotus Skin Spa' }, areaServed: { '@type': 'City', name: 'Dallas' } },
              { '@type': 'Service', name: 'Specialty Treatments', serviceType: 'Specialty Spa Treatment', provider: { '@type': 'HealthAndBeautyBusiness', name: 'Healing Lotus Skin Spa' }, areaServed: { '@type': 'City', name: 'Dallas' } },
            ],
          }),
        }}
      />
      {/* Hero Section */}
      <section className="relative h-[50vh] flex items-center justify-center overflow-hidden">
        <div className="absolute inset-0 bg-[#232347]/60 z-10"></div>
        <div className="relative z-20 text-center px-4">
          <h1 className="text-4xl md:text-6xl font-bold text-white mb-4 font-serif">Our Spa Services in Dallas</h1>
          <p className="text-xl text-white/90 max-w-2xl mx-auto">Indulge in luxury treatments tailored to your needs</p>
        </div>
        <div className="absolute inset-0 z-0">
          <img 
            src="https://images.pexels.com/photos/3757942/pexels-photo-3757942.jpeg?auto=compress&cs=tinysrgb&w=1260&h=750&dpr=1" 
            alt="Spa services" 
            className="w-full h-full object-cover"
          />
        </div>
      </section>

      {/* Services Sections */}
      <div className="container mx-auto py-16 px-4 md:px-6">
        {serviceCategories.map((category, index) => (
          <section 
            key={category.id} 
            id={category.id}
            className={`py-16 ${index !== 0 ? 'border-t border-[#B2D8C5]/30' : ''}`}
          >
            <div className={`flex flex-col ${index % 2 === 0 ? 'lg:flex-row' : 'lg:flex-row-reverse'} gap-8 items-center`}>
              {/* Image Column */}
              <div className="w-full lg:w-1/2 rounded-xl overflow-hidden shadow-xl">
                <img 
                  src={category.image || category.image_url} 
                  alt={category.title || category.name}
                  className="w-full h-[400px] object-cover"
                />
              </div>
              
              {/* Content Column */}
              <div className="w-full lg:w-1/2">
                <h2 className="text-3xl md:text-4xl font-serif font-semibold text-[#232347] mb-4">{category.title || category.name}</h2>
                {/* Display category description with promotional text highlighted */}
                <div className="mb-8 leading-relaxed">
                  {category.description && (() => {
                    const paragraphs = category.description.split('\n\n');
                    return paragraphs.map((paragraph, idx) => {
                      // First paragraph might be promotional text
                      const isPromo = idx === 0 && (
                        paragraph.includes('Buy') || 
                        paragraph.includes('Purchase') || 
                        paragraph.includes('Get') ||
                        paragraph.includes('Complimentary')
                      );
                      
                      return (
                        <p 
                          key={idx} 
                          className={`mb-4 last:mb-0 ${
                            isPromo 
                              ? 'text-[#232347] font-semibold text-lg bg-gradient-to-r from-[#F7CAC9]/20 to-[#B2D8C5]/20 p-3 rounded-lg border-l-4 border-[#B2D8C5]' 
                              : 'text-[#232347]/80'
                          }`}
                        >
                          {paragraph}
                        </p>
                      );
                    });
                  })()}
                </div>
                
                {category.services.length > 0 ? (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {category.services.map((service) => (
                      <ServiceCard key={service.id} service={service} />
                    ))}
                  </div>
                ) : (
                  <p className="text-[#232347]/60 italic">No services currently available in this category.</p>
                )}
                
                {index === serviceCategories.length - 1 ? null : (
                  <div className="mt-8 text-center">
                    <a 
                      href={`#${serviceCategories[index + 1].id}`} 
                      className="inline-flex items-center justify-center bg-[#F7CAC9] text-white px-6 py-3 rounded-full font-medium hover:bg-[#e6b9b8] transition-colors"
                    >
                      Explore {serviceCategories[index + 1].title}
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 ml-2" viewBox="0 0 20 20" fill="currentColor">
                        <path fillRule="evenodd" d="M16.707 10.293a1 1 0 010 1.414l-6 6a1 1 0 01-1.414 0l-6-6a1 1 0 111.414-1.414L10 14.586l5.293-5.293a1 1 0 011.414 0z" clipRule="evenodd" />
                      </svg>
                    </a>
                  </div>
                )}
              </div>
            </div>
          </section>
        ))}
      </div>
      
      {/* Call to Action */}
      <section className="bg-[#232347] text-white py-16 px-4">
        <div className="container mx-auto text-center">
          <h2 className="text-3xl md:text-4xl font-serif font-semibold mb-4">Ready to Indulge?</h2>
          <p className="max-w-2xl mx-auto mb-8 text-white/80">Book your appointment today and experience the ultimate in relaxation and rejuvenation.</p>
          <button 
            onClick={() => navigate('/booking')}
            className="bg-[#B2D8C5] hover:bg-[#88bfa3] text-white px-8 py-3 rounded-md font-semibold text-lg transition-colors"
          >
            Book An Appointment
          </button>
        </div>
      </section>
    </div>
  );
}

export default Services;